import React, { useRef, useEffect } from "react";

// Parse hex color to {r, g, b}
const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
};

export const GalacticBackground = ({ color1, color2 }) => {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const animRef = useRef(null);

  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Generate stars
    const starCount = 160;
    starsRef.current = Array.from({ length: starCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 0.5,
      opacity: Math.random(),
      speed: Math.random() * 0.008 + 0.002,
      direction: Math.random() > 0.5 ? 1 : -1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const star of starsRef.current) {
        star.opacity += star.speed * star.direction;
        if (star.opacity >= 1) {
          star.opacity = 1;
          star.direction = -1;
        } else if (star.opacity <= 0.15) {
          star.opacity = 0.15;
          star.direction = 1;
        }

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener("resize", resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  // Recalculate star positions on resize
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      for (const star of starsRef.current) {
        star.x = Math.random() * canvas.width;
        star.y = Math.random() * canvas.height;
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      className="galactic-bg"
      style={{
        background: `radial-gradient(ellipse at 30% 40%, rgba(${c2.r}, ${c2.g}, ${c2.b}, 1) 0%, #050510 70%)`,
      }}
    >
      {/* Nebulas */}
      <div
        className="nebula nebula-1"
        style={{
          background: `radial-gradient(circle, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.18) 0%, transparent 70%)`,
          filter: "blur(80px)",
        }}
      />
      <div
        className="nebula nebula-2"
        style={{
          background: `radial-gradient(circle, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.12) 0%, rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.08) 50%, transparent 70%)`,
          filter: "blur(100px)",
        }}
      />
      <div
        className="nebula nebula-3"
        style={{
          background: `radial-gradient(circle, rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.22) 0%, transparent 70%)`,
          filter: "blur(90px)",
        }}
      />
      <div
        className="nebula nebula-4"
        style={{
          background: `radial-gradient(circle, rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.1) 0%, transparent 60%)`,
          filter: "blur(70px)",
        }}
      />

      {/* Ethereal circles */}
      <div
        className="ethereal-circle circle-1"
        style={{
          border: `1px solid rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.12)`,
          background: `rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.02)`,
        }}
      />
      <div
        className="ethereal-circle circle-2"
        style={{
          border: `1px solid rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.18)`,
          background: `rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.03)`,
        }}
      />
      <div
        className="ethereal-circle circle-3"
        style={{
          border: `1px solid rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.1)`,
          background: `rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.02)`,
        }}
      />
      <div
        className="ethereal-circle circle-4"
        style={{
          border: `1.5px solid rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.22)`,
          background: `rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.04)`,
        }}
      />
      <div
        className="ethereal-circle circle-5"
        style={{
          border: `1px solid rgba(${c2.r}, ${c2.g}, ${c2.b}, 0.08)`,
        }}
      />
      <div
        className="ethereal-circle circle-6"
        style={{
          border: `1px solid rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.15)`,
          background: `rgba(${c1.r}, ${c1.g}, ${c1.b}, 0.025)`,
        }}
      />

      {/* Stars canvas */}
      <canvas ref={canvasRef} />
    </div>
  );
};

export default GalacticBackground;
